import { memo, forwardRef } from 'react';
import styles from './shield-hud.module.css';

export const SHIELD_ICON_SRC = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 72"><defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#6ee7d9"/><stop offset="45%" stop-color="#22d3ee"/><stop offset="100%" stop-color="#0891b2"/></linearGradient><filter id="glow"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs><path d="M32 4 L54 14 V36 C54 52 32 66 32 66 C32 66 10 52 10 36 V14 Z" fill="url(#sg)" stroke="#67e8f9" stroke-width="1.5" filter="url(#glow)"/><path d="M24 36 L30 42 L42 28" fill="none" stroke="#ecfeff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.95"/></svg>`)}`;

const RING_RADIUS = 16;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export interface ShieldHudProps {
  total: number;
  max: number;
  /** Pulse the count when it just increased. */
  pulse?: boolean;
  /** Version compacte pour la barre de navigation. */
  compact?: boolean;
  /** Affiche la barre de progression (désactivée par défaut). */
  showProgressBar?: boolean;
}

export const ShieldHud = memo(forwardRef<HTMLDivElement, ShieldHudProps>(function ShieldHud(
  { total, max, pulse = false, compact = false, showProgressBar = false },
  ref
) {
  const fillPercent = max > 0 ? Math.min(100, (total / max) * 100) : 0;
  const dashOffset = RING_CIRCUMFERENCE * (1 - fillPercent / 100);

  if (compact) {
    return (
      <div
        ref={ref}
        className={`${styles.hudRing} ${pulse ? styles.recoit : ''}`.trim()}
        aria-label={`${total} bouclier${total !== 1 ? 's' : ''} pédagogique${total !== 1 ? 's' : ''} sur ${max}`}
      >
        <svg viewBox="0 0 40 40" width="32" height="32" aria-hidden>
          <circle
            cx="20"
            cy="20"
            r={RING_RADIUS}
            fill="none"
            stroke="rgba(255,255,255,.2)"
            strokeWidth="3"
          />
          <circle
            cx="20"
            cy="20"
            r={RING_RADIUS}
            fill="none"
            stroke="#c8a24a"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 20 20)"
          />
          <text
            x="20"
            y="24"
            textAnchor="middle"
            className={styles.ringGlyph}
          >
            {total}
          </text>
        </svg>
        <div className={styles.ringMeta}>
          <span className={styles.ringLabel}>BOUCLIERS</span>
          <span className={styles.ringCount}>
            {total} / {max}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={styles.hud}
      aria-label={`${total} bouclier${total !== 1 ? 's' : ''} pédagogique${total !== 1 ? 's' : ''} sur ${max}`}
    >
      <img src={SHIELD_ICON_SRC} alt="" className={styles.icon} aria-hidden />
      <span className={`${styles.count} ${pulse ? styles.countPulse : ''}`}>
        {total}/{max}
      </span>
      {showProgressBar && (
        <div className={styles.barTrack} aria-hidden>
          <div
            className={styles.barFill}
            style={{ width: `${fillPercent}%` }}
          />
          <div className={styles.barGlow} style={{ width: `${fillPercent}%` }} />
        </div>
      )}
    </div>
  );
}));
